// api.js
import axios from 'axios';

// Use dynamic or relative backend URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  withCredentials: false,
});

// Token management
let authToken = localStorage.getItem('authToken') || null;
const TOKEN_MAX_AGE_MS = 48 * 60 * 60 * 1000; // 48 hours

function getTokenAgeMs() {
  const setAt = parseInt(localStorage.getItem('authTokenSetAt') || '0', 10);
  if (!setAt) return Number.MAX_SAFE_INTEGER;
  return Date.now() - setAt;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const errorData = error.response?.data;

    // Enhanced error logging for debugging - removed for security

    // Treat 401 as session expired and redirect to login
    if (status === 401) {
      clearAuthToken();
      window.location.href = '/';
    }

    // Add user-friendly error messages
    if (status >= 500) {
      error.userMessage = 'Server error. Please try again in a moment.';
    } else if (status === 400) {
      error.userMessage = errorData?.detail || 'Invalid request. Please check your input.';
    } else if (status === 401) {
      error.userMessage = 'Session expired. Please log in again.';
    } else if (status === 403) {
      error.userMessage = errorData?.detail || 'Insufficient permissions to perform this action.';
    } else if (status === 404) {
      error.userMessage = 'Resource not found.';
    } else if (status === 409) {
      error.userMessage = errorData?.detail || 'Conflict. Item may already exist.';
    }

    return Promise.reject(error);
  }
);

// Set auth token with validation
export function setAuthToken(token) {
  authToken = token || null;
  if (token) {
    try {
      // Basic JWT validation
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      localStorage.setItem('authToken', token);
      localStorage.setItem('authTokenSetAt', Date.now().toString());
      return true;
    } catch (e) {
      return false;
    }
  } else {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authTokenSetAt');
    return true;
  }
}

// Clear auth token
export function clearAuthToken() {
  authToken = null;
  localStorage.removeItem('authToken');
  localStorage.removeItem('authTokenSetAt');
  localStorage.removeItem('user');
}

// -----------------------------
// Settings (Phase 1 - Read Only)
// -----------------------------
export async function listInflowSourcesApi() {
  return api.get('/api/settings/inflow-sources');
}

export async function listBankAccountsApi() {
  return api.get('/api/settings/bank-accounts');
}

export async function listOutflowCategoriesApi() {
  return api.get('/api/settings/outflow-categories');
}

export async function listOutflowSubcategoriesApi() {
  return api.get('/api/settings/outflow-subcategories');
}

// -----------------------------
// Settings (Phase 2 - CRUD, protected Owner/IT Admin)
// -----------------------------
// Inflow Sources
export async function createInflowSourceApi(payload) {
  // payload: { name }
  return api.post('/api/settings/inflow-sources', payload);
}

export async function updateInflowSourceApi(id, payload) {
  // payload: { name?, is_active? }
  return api.put(`/api/settings/inflow-sources/${id}`, payload);
}

export async function deleteInflowSourceApi(id) {
  return api.delete(`/api/settings/inflow-sources/${id}`);
}

// Bank Accounts
export async function createBankAccountApi(payload) {
  // payload: { name, is_default_cash? }
  return api.post('/api/settings/bank-accounts', payload);
}

export async function updateBankAccountApi(id, payload) {
  // payload: { name?, is_active?, is_default_cash? }
  return api.put(`/api/settings/bank-accounts/${id}`, payload);
}

export async function deleteBankAccountApi(id) {
  return api.delete(`/api/settings/bank-accounts/${id}`);
}

// Bank Limits (CRUD + usage/check)
export async function listBankLimitsApi() {
  return api.get('/api/settings/bank-limits');
}

export async function upsertBankLimitApi(bankAccountId, payload) {
  // payload: { monthly_limit_rs?, quarterly_limit_rs?, yearly_limit_rs?, is_active? }
  return api.put(`/api/settings/bank-limits/${bankAccountId}`, payload);
}

export async function deleteBankLimitApi(bankAccountId) {
  return api.delete(`/api/settings/bank-limits/${bankAccountId}`);
}

export async function getLimitsUsageApi(period = 'monthly', anchor /* YYYY-MM-DD or undefined */) {
  const params = { period };
  if (anchor) params.anchor = anchor;
  return api.get('/api/limits/usage', { params });
}

export async function checkBankLimitApi({ bank, amount, date }) {
  // { bank: string, amount: number, date: 'YYYY-MM-DD' }
  return api.get('/api/limits/check', { params: { bank, amount, date } });
}

// Outflow Categories
export async function createOutflowCategoryApi(payload) {
  // payload: { name }
  return api.post('/api/settings/outflow-categories', payload);
}

export async function updateOutflowCategoryApi(id, payload) {
  // payload: { name?, is_active? }
  return api.put(`/api/settings/outflow-categories/${id}`, payload);
}

export async function deleteOutflowCategoryApi(id) {
  return api.delete(`/api/settings/outflow-categories/${id}`);
}

// Outflow Subcategories
export async function createOutflowSubcategoryApi(payload) {
  // payload: { name, category_id, accounting_treatment? }
  return api.post('/api/settings/outflow-subcategories', payload);
}

export async function updateOutflowSubcategoryApi(id, payload) {
  // payload: { name?, category_id?, is_active?, accounting_treatment? }
  return api.put(`/api/settings/outflow-subcategories/${id}`, payload);
}

export async function deleteOutflowSubcategoryApi(id) {
  return api.delete(`/api/settings/outflow-subcategories/${id}`);
}

export async function syncOutflowSubcategoryTreatmentApi(id) {
  return api.post(`/api/settings/outflow-subcategories/${id}/sync-treatment`);
}

// Add Cash Inflow with file upload
export async function addCashInflow(form) {
  const data = new FormData();
  data.append('date', form.date);
  data.append('reflecting_month', form.reflecting_month);
  data.append('source', form.source);
  data.append('amount_rs', form.amount_rs);
  if (form.amount_usd) data.append('amount_usd', form.amount_usd);
  data.append('bank', form.bank);
  data.append('reflecting_bank', form.reflecting_bank);
  data.append('request_inward', form.request_inward);
  if (form.invoice_file) data.append('invoice_file', form.invoice_file);
  if (form.remark) data.append('remark', form.remark);

  return api.post('/api/inflow', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function addCashOutflow(form) {
  const data = new FormData();
  data.append('date', form.date);
  data.append('reflecting_month', form.reflecting_month);
  data.append('expense', form.expense);
  data.append('amount_rs', form.amount_rs);
  data.append('sub_category', form.sub_category);
  data.append('category', form.category);
  data.append('deducted_from', form.deducted_from);
  data.append('mode', form.mode);
  data.append('nature', form.nature || '');
  data.append('vendor', form.vendor || '');
  data.append('remark', form.remark || '');
  // If subcategory is Other, also send free-text category to backend
  if ((form.sub_category || '').toLowerCase() === 'other' && form.category) {
    data.append('category_free_text', form.category);
  }
  if (form.invoice_file) {
    data.append('invoice_file', form.invoice_file);
  }
  return api.post('/api/outflow', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

export async function updateCashInflow(id, form) {
  const data = new FormData();
  data.append('date', form.date);
  data.append('reflecting_month', form.reflecting_month);
  data.append('source', form.source);
  data.append('amount_rs', form.amount_rs);
  data.append('amount_usd', form.amount_usd || '');
  data.append('bank', form.bank);
  data.append('reflecting_bank', form.reflecting_bank);
  data.append('request_inward', form.request_inward ? 'true' : 'false');
  data.append('remark', form.remark || '');
  if (form.invoice_file) {
    data.append('invoice_file', form.invoice_file);
  }
  return api.put(`/api/inflow/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

export async function updateCashOutflow(id, form) {
  const data = new FormData();
  data.append('date', form.date);
  data.append('reflecting_month', form.reflecting_month);
  data.append('expense', form.expense);
  data.append('amount_rs', form.amount_rs);
  data.append('sub_category', form.sub_category);
  data.append('category', form.category);
  data.append('deducted_from', form.deducted_from);
  data.append('mode', form.mode);
  data.append('nature', form.nature || '');
  data.append('vendor', form.vendor || '');
  data.append('remark', form.remark || '');
  // If subcategory is Other, also send free-text category to backend
  if ((form.sub_category || '').toLowerCase() === 'other' && form.category) {
    data.append('category_free_text', form.category);
  }
  if (form.invoice_file) {
    data.append('invoice_file', form.invoice_file);
  }
  return api.put(`/api/outflow/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

// Get inflows (protected)
export async function getInflows(period = 'this_month', opts = {}) {
  const params = { period };
  if (opts.start) params.start_date = opts.start;
  if (opts.end) params.end_date = opts.end;
  return api.get('/api/inflow', { params });
}

// Get inflows paginated (server-side pagination/sorting/filtering)
export async function getInflowsPaginated({
  period = 'this_month', start, end, limit = 25, offset = 0,
  sort_by = 'date', sort_dir = 'desc', q, source, bank, request_inward,
  min_amount, max_amount,
} = {}) {
  const params = { period, limit, offset, sort_by, sort_dir };
  if (start) params.start_date = start;
  if (end) params.end_date = end;
  if (q) params.q = q;
  if (source) params.source = source;
  if (bank) params.bank = bank;
  if (request_inward !== undefined && request_inward !== null && request_inward !== '') {
    // Convert boolean to string for backend compatibility
    if (typeof request_inward === 'boolean') {
      params.request_inward = request_inward ? 'yes' : 'no';
    } else {
      params.request_inward = request_inward;
    }
  }
  if (min_amount != null) params.min_amount = min_amount;
  if (max_amount != null) params.max_amount = max_amount;
  return api.get('/api/inflow/paginated', { params });
}

// Get outflows (protected)
export async function getOutflows(period = 'this_month', opts = {}) {
  const params = { period };
  if (opts.start) params.start_date = opts.start;
  if (opts.end) params.end_date = opts.end;
  return api.get('/api/outflow', { params });
}

// Get outflows paginated (server-side pagination/sorting/filtering)
export async function getOutflowsPaginated({
  period = 'this_month', start, end, limit = 25, offset = 0,
  sort_by = 'date', sort_dir = 'desc', q, category, sub_category, mode, vendor, deducted_from,
  min_amount, max_amount,
} = {}) {
  const params = { period, limit, offset, sort_by, sort_dir };
  if (start) params.start_date = start;
  if (end) params.end_date = end;
  if (q) params.q = q;
  if (category) params.category = category;
  if (sub_category) params.sub_category = sub_category;
  if (mode) params.mode = mode;
  if (vendor) params.vendor = vendor;
  if (deducted_from) params.deducted_from = deducted_from;
  if (min_amount != null) params.min_amount = min_amount;
  if (max_amount != null) params.max_amount = max_amount;
  return api.get('/api/outflow/paginated', { params });
}

// Financial year months (protected)
export async function getFinancialYearMonths() {
  return api.get('/api/financial-year-months');
}

// Analytics (protected)
export async function getAnalytics(period = 'this_month', opts = {}) {
  const params = { period };
  if (opts.start) params.start_date = opts.start;
  if (opts.end) params.end_date = opts.end;
  return api.get('/api/analytics', { params });
}

// Dashboard data (protected)
export async function getDashboardData(period = 'this_month', opts = {}) {
  const params = { period };
  if (opts.start) params.start_date = opts.start;
  if (opts.end) params.end_date = opts.end;
  return api.get('/api/dashboard', { params });
}

// Recent transactions (protected)
export async function getRecentTransactions(limit = 10) {
  return api.get(`/api/recent-transactions?limit=${limit}`);
}

// Delete inflow
export async function deleteInflow(id) {
  return api.delete(`/api/inflow/${id}`);
}

// Delete outflow
export async function deleteOutflow(id) {
  return api.delete(`/api/outflow/${id}`);
}

// -----------------------------
// Insights (server-side)
// -----------------------------
export async function insightsPreview(payload) {
  // payload: { period, root, pagination?, sort? }
  return api.post('/api/insights/preview', payload);
}

export async function insightsRun(payload) {
  // payload: { period, root, viz }
  return api.post('/api/insights/run', payload);
}

// Separate previews for inflow and outflow
export async function insightsPreviewInflow(payload) {
  return api.post('/api/insights/preview/inflow', payload);
}

export async function insightsPreviewOutflow(payload) {
  return api.post('/api/insights/preview/outflow', payload);
}

// Login (returns temp token and requires OTP)
export async function login(email, password) {
  const form = new URLSearchParams();
  form.append('username', email);
  form.append('password', password);
  form.append('grant_type', 'password');
  
  return api.post('/auth/login', form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

// Verify OTP and get full access token
export async function verifyOTP(email, otpCode) {
  return api.post('/auth/verify-otp', {
    email: email,
    otp_code: otpCode
  });
}

// Logout
export function logout() {
  clearAuthToken();
}

// Users Management (protected)
export async function listUsers() {
  return api.get('/api/users');
}

export async function createUserApi(payload) {
  // payload: { email, password, role, name?, otp_enabled?, is_active? }
  return api.post('/api/users', payload);
}

export async function updateUserApi(userId, payload) {
  // payload: partial update fields { email?, password?, role?, name?, otp_enabled?, is_active? }
  return api.put(`/api/users/${userId}`, payload);
}

export async function deleteUserApi(userId) {
  return api.delete(`/api/users/${userId}`);
}

export async function toggleUserOtpApi(userId) {
  return api.post(`/api/users/${userId}/toggle-otp`);
}

export default api;

// -----------------------------
// Automation: Recurring Transactions
// -----------------------------
// CRUD
export async function listRecurringItemsApi(onlyActive = false) {
  return api.get('/api/automation/recurring-items', { params: { only_active: onlyActive } });
}

export async function createRecurringItemApi(payload) {
  // payload: { name, amount_rs, schedule_type: 'monthly'|'quarterly'|'yearly_financial', day_of_month? }
  return api.post('/api/automation/recurring-items', payload);
}

export async function updateRecurringItemApi(id, payload) {
  return api.put(`/api/automation/recurring-items/${id}`, payload);
}

export async function deleteRecurringItemApi(id) {
  // archive (soft-delete)
  return api.delete(`/api/automation/recurring-items/${id}`);
}

// Reminders & mark-paid
export async function getRecurringRemindersApi(windowDays = 30) {
  return api.get('/api/automation/reminders', { params: { window_days: windowDays } });
}

export async function markRecurringItemPaidApi(id, paidOn /* YYYY-MM-DD or undefined */, occurrenceId /* number or undefined */) {
  const params = {};
  if (paidOn) params.paid_on = paidOn;
  if (occurrenceId) params.occurrence_id = occurrenceId;
  return api.post(`/api/automation/recurring-items/${id}/mark-paid`, null, { params });
}

export async function skipRecurringOccurrenceApi(itemId, occurrenceId) {
  return api.post(`/api/automation/recurring-items/${itemId}/skip`, null, { params: { occurrence_id: occurrenceId } });
}

export async function unskipRecurringOccurrenceApi(itemId, occurrenceId) {
  return api.post(`/api/automation/recurring-items/${itemId}/unskip`, null, { params: { occurrence_id: occurrenceId } });
}

// -----------------------------
// Automation: Backup & Export
// -----------------------------
// BACKUP FUNCTIONALITY DISABLED - Future Updates
// Backup settings
// export async function getBackupSettingsApi() {
//   return api.get('/api/automation/backup/settings');
// }

// export async function updateBackupSettingsApi(payload) {
//   // payload: { enabled?, time_ist?, recipients_csv?, retention_days? }
//   return api.put('/api/automation/backup/settings', payload);
// }

// // Run backup now
// export async function runBackupNowApi() {
//   return api.post('/api/automation/backup/run-now');
// }

// // Backup history
// export async function listBackupHistoryApi(limit = 20, offset = 0) {
//   return api.get('/api/automation/backup/history', { params: { limit, offset } });
// }

// Manual export download (CSV zipped by backend if applicable)
export async function downloadExportApi({ includeInflows = true, includeOutflows = true, all = false, fromDate, toDate }) {
  const params = {
    include_inflows: includeInflows,
    include_outflows: includeOutflows,
    all: !!all,
  };
  if (!all) {
    if (fromDate) params.start_date = fromDate; // backend expects start_date
    if (toDate) params.end_date = toDate; // backend expects end_date
  }
  return api.get('/api/automation/export/download', {
    params,
    responseType: 'blob',
  });
}

export async function importInflowsApi(rows) {
  return api.post('/api/import-inflows', rows);
}

export async function importOutflowsApi(rows) {
  return api.post('/api/import-outflows', rows);
}

// -----------------------------
// Reports (CRUD)
// -----------------------------
export async function listReportsApi() {
  return api.get('/api/reports');
}

export async function createReportApi(payload) {
  // payload keys: name, description?, type, category, tags[], inflow_rules[], outflow_rules[], inflow_group_by, outflow_group_by
  return api.post('/api/reports', payload);
}

export async function getReportApi(id) {
  return api.get(`/api/reports/${id}`);
}

export async function updateReportApi(id, payload) {
  return api.put(`/api/reports/${id}`, payload);
}

export async function deleteReportApi(id) {
  return api.delete(`/api/reports/${id}`);
}

export async function toggleUserLoginApi(id) {
  return api.post(`/api/users/${id}/toggle-login`);
}

// Delete All Data
export async function deleteAllDataApi(confirmation) {
  return api.delete(`/api/users/delete-all-data?confirmation=${confirmation}`);
}
